import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ChevronLeft,
  Bell,
  Trash2,
  Info,
  Heart,
  Globe,
} from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { useCart } from '@/store/cart.store';

const APP_VERSION = '1.0.0';
const NOTIF_KEY = 'resstro_menu_notifications';

export default function Settings() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.accessToken);
  const clearCart = useCart((s) => s.clear);

  const [notifications, setNotifications] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(NOTIF_KEY) !== '0';
  });

  useEffect(() => {
    localStorage.setItem(NOTIF_KEY, notifications ? '1' : '0');
  }, [notifications]);

  if (!token) {
    navigate('/login', { replace: true });
    return null;
  }

  const clearCache = () => {
    if (!confirm('Очистить локальный кеш (корзина, кешированные запросы)? Аккаунт и заказы не пострадают.')) {
      return;
    }
    clearCart();
    qc.clear();
    toast.success('Кеш очищен');
  };

  return (
    <div className="pt-4 pb-8">
      <div className="px-4 mb-4 flex items-center gap-2">
        <Link to="/profile" className="btn-ghost -ml-2 px-2">
          <ChevronLeft size={20} />
        </Link>
        <h1 className="text-2xl font-semibold">Настройки</h1>
      </div>

      <div className="px-4 space-y-3">
        <div className="card divide-y divide-slate-100 overflow-hidden">
          <ToggleRow
            icon={<Bell size={18} />}
            iconBg="bg-amber-50 text-amber-600"
            title="Уведомления о заказе"
            subtitle="Оповещать о смене статуса"
            value={notifications}
            onChange={setNotifications}
          />
          <InfoRow
            icon={<Globe size={18} />}
            iconBg="bg-emerald-50 text-emerald-600"
            title="Язык"
            value="Русский"
          />
        </div>

        <div className="card divide-y divide-slate-100 overflow-hidden">
          <Link to="/favorites" className="p-4 flex items-center gap-3 active:bg-slate-50">
            <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center shrink-0">
              <Heart size={18} />
            </div>
            <div className="flex-1">
              <div className="font-medium">Избранное</div>
              <div className="text-xs text-slate-500">Ваши любимые блюда</div>
            </div>
          </Link>
          <button
            onClick={clearCache}
            className="w-full text-left p-4 flex items-center gap-3 active:bg-slate-50"
          >
            <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
              <Trash2 size={18} />
            </div>
            <div className="flex-1">
              <div className="font-medium">Очистить кеш</div>
              <div className="text-xs text-slate-500">Сбрасывает корзину и кешированные запросы</div>
            </div>
          </button>
        </div>

        <div className="card divide-y divide-slate-100 overflow-hidden">
          <InfoRow
            icon={<Info size={18} />}
            iconBg="bg-brand-50 text-brand-600"
            title="О приложении"
            value={`v${APP_VERSION}`}
          />
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  icon, iconBg, title, subtitle, value, onChange,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="p-4 flex items-center gap-3 cursor-pointer active:bg-slate-50">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{title}</div>
        {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
      </div>
      <span
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
          value ? 'bg-brand-600' : 'bg-slate-300'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            value ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
        <input
          type="checkbox"
          className="sr-only"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
        />
      </span>
    </label>
  );
}

function InfoRow({
  icon, iconBg, title, value,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  value: string;
}) {
  return (
    <div className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{title}</div>
      </div>
      <div className="text-sm text-slate-500">{value}</div>
    </div>
  );
}
